import { AlertTriangle } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";

import { CARD_STATUS_TKEY, isIrreversibleStatus, isValidCardStatus } from "../utils/cardStatus";

interface UpdateCardStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentStatus?: string;
  newStatus?: string;
  isLoading?: boolean;
}

export const UpdateCardStatusModal = ({
  isOpen,
  onClose,
  onConfirm,
  currentStatus,
  newStatus,
  isLoading = false,
}: UpdateCardStatusModalProps) => {
  const { t } = useTranslations("cards");

  const getStatusLabel = (status: string | undefined): string => {
    if (!status) return "";
    return isValidCardStatus(status) ? t(CARD_STATUS_TKEY[status] as never) : status;
  };

  const currentStatusLabel = getStatusLabel(currentStatus);
  const newStatusLabel = getStatusLabel(newStatus);

  const isIrreversible = isIrreversibleStatus(newStatus);

  return (
    <ColocaDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      size="default"
      title={t("update_status_modal_title")}
      footer={
        <>
          <ColocaButton variant="ghost" onClick={onClose} disabled={isLoading}>
            {t("update_status_modal_button_cancel")}
          </ColocaButton>
          {/* Disabling or deleting a card cannot be undone: destructive, like the alert above. */}
          <ColocaButton
            variant={isIrreversible ? "destructive" : "primary"}
            onClick={onConfirm}
            loading={isLoading}
            disabled={isLoading}
          >
            {t("update_status_modal_button_confirm")}
          </ColocaButton>
        </>
      }
    >
      <div className="space-y-4">
        <ColocaAlert
          variant={isIrreversible ? "destructive" : "warning"}
          icon={AlertTriangle}
          heading={
            isIrreversible
              ? t("update_status_modal_irreversible_title")
              : t("update_status_modal_warning_title")
          }
          text={isIrreversible ? t("update_status_modal_irreversible_body") : undefined}
        />

        <p className="text-sm text-muted-foreground">
          {t("update_status_modal_confirmation_message")}
          {currentStatusLabel && (
            <>
              {" "}
              <span className="font-semibold text-foreground">&ldquo;{currentStatusLabel}&rdquo;</span>
            </>
          )}
          {newStatusLabel && (
            <>
              {" "}
              {t("update_status_modal_to")}{" "}
              <span className="font-semibold text-foreground">&ldquo;{newStatusLabel}&rdquo;</span>
            </>
          )}
          ?
        </p>
      </div>
    </ColocaDialog>
  );
};
